import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Calendar as CalendarIcon, Info, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { AdSlot } from '../components/ads/AdSlot';
interface MoonCalendarPageProps {
  onNavigate: (page: string) => void;
}
export function MoonCalendarPage({
  onNavigate
}: MoonCalendarPageProps) {
  const currentDate = new Date().toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-20947">
      {/* Header */}
      <div className="bg-slate-900 text-white pt-6 pb-16 relative overflow-hidden" data-id="element-20948">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-20949" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-20950" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-20951">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Лунный календарь'
        }]} className="mb-8 text-slate-300" data-id="element-20952" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-20953">
            <div className="max-w-2xl" data-id="element-20954">
              <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-20955">
                Обновляется ежедневно
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-id="element-20956">
                Лунный календарь
              </h1>
              <p className="text-xl text-slate-300 mb-2 capitalize" data-id="element-20957">
                {currentDate}
              </p>
              <p className="text-slate-400" data-id="element-20958">
                Следите за фазами Луны, чтобы планировать важные дела, стрижки,
                посадки и понимать общий энергетический фон дня.
              </p>
            </div>

            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 120,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-48 h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-400 shadow-[0_0_50px_rgba(255,255,255,0.2)] flex items-center justify-center relative overflow-hidden" data-id="element-20959">
              {/* Simple moon phase representation (Waxing Gibbous) */}
              <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-80 mix-blend-multiply" data-id="element-20960" />
              <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-20961" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8 relative z-20" data-id="element-20962">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-20963">
          {/* Left Column: Current Status */}
          <div className="lg:col-span-2 space-y-8" data-id="element-20964">
            <Card className="p-6 sm:p-8 shadow-xl border-0" data-id="element-20965">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-20966">
                Энергия сегодняшнего дня
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-20967">
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20968">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20969">
                    Фаза Луны
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20970">
                    Растущая Луна <span className="text-indigo-500" data-id="element-20971">🌔</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-20972">
                    Освещенность: 78%
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20973">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20974">
                    Лунный день
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20975">
                    11-й лунный день
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-20976">
                    Символ: Огненный меч
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-20977">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-20978">
                    Луна в знаке
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-20979">
                    Лев <span className="text-amber-500" data-id="element-20980">♌</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-20981">
                    Стихия: Огонь
                  </div>
                </div>

                <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100" data-id="element-20982">
                  <div className="text-sm text-rose-600 font-medium mb-1 flex items-center gap-1" data-id="element-20983">
                    <AlertCircle className="w-4 h-4" data-id="element-20984" /> Внимание
                  </div>
                  <div className="text-xl font-bold text-slate-900" data-id="element-20985">
                    Луна без курса
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-20986">
                    С 14:30 до 17:45 (МСК)
                  </div>
                </div>
              </div>

              <div className="prose prose-slate max-w-none" data-id="element-20987">
                <p data-id="element-20988">
                  Один из самых энергетически мощных дней лунного месяца.
                  Энергия бьет ключом, пробуждаются скрытые резервы организма.
                  Отличный день для активных действий, начала новых проектов и
                  физических нагрузок.
                </p>
                <p data-id="element-20989">
                  Луна во Льве добавляет желания быть в центре внимания, творить
                  и радовать себя. Однако в период "Луны без курса" (с 14:30 до
                  17:45) лучше воздержаться от принятия важных решений и
                  подписания документов — дела, начатые в это время, могут пойти
                  не по плану.
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-20990">
              <Card className="p-6 border-emerald-200 bg-emerald-50/30" data-id="element-20991">
                <div className="flex items-center gap-2 mb-4" data-id="element-20992">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-20993" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-20994">
                    Благоприятно
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-20995">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20996">
                    <span className="text-emerald-500 mt-0.5" data-id="element-20997">•</span>{' '}
                    Творческие начинания
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-20998">
                    <span className="text-emerald-500 mt-0.5" data-id="element-20999">•</span>{' '}
                    Физическая активность и спорт
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21000">
                    <span className="text-emerald-500 mt-0.5" data-id="element-21001">•</span> Стрижка
                    волос (для быстрого роста)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21002">
                    <span className="text-emerald-500 mt-0.5" data-id="element-21003">•</span> Покупки
                    одежды и украшений
                  </li>
                </ul>
              </Card>

              <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-21004">
                <div className="flex items-center gap-2 mb-4" data-id="element-21005">
                  <XCircle className="w-6 h-6 text-rose-500" data-id="element-21006" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-21007">
                    Не рекомендуется
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-21008">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21009">
                    <span className="text-rose-500 mt-0.5" data-id="element-21010">•</span> Пассивность
                    и лень
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21011">
                    <span className="text-rose-500 mt-0.5" data-id="element-21012">•</span>{' '}
                    Хирургические операции на сердце
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21013">
                    <span className="text-rose-500 mt-0.5" data-id="element-21014">•</span> Важные дела
                    в период "Луны без курса"
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-21015">
                    <span className="text-rose-500 mt-0.5" data-id="element-21016">•</span> Конфликты с
                    начальством
                  </li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Right Column: Mini Calendar & Info */}
          <div className="space-y-6" data-id="element-21017">
            <Card className="p-6" data-id="element-21018">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-21019">
                <CalendarIcon className="w-5 h-5 text-indigo-500" data-id="element-21020" />
                Ближайшие фазы
              </h3>
              <div className="space-y-4" data-id="element-21021">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-21022">
                  <div className="flex items-center gap-3" data-id="element-21023">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-21024">
                      🌕
                    </div>
                    <div data-id="element-21025">
                      <div className="text-sm font-bold text-slate-900" data-id="element-21026">
                        Полнолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-21027">в Деве</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-21028">
                    24 фев
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-21029">
                  <div className="flex items-center gap-3" data-id="element-21030">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-21031">
                      🌗
                    </div>
                    <div data-id="element-21032">
                      <div className="text-sm font-bold text-slate-900" data-id="element-21033">
                        Третья четверть
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-21034">в Стрельце</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-21035">
                    3 мар
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-21036">
                  <div className="flex items-center gap-3" data-id="element-21037">
                    <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-lg" data-id="element-21038">
                      🌑
                    </div>
                    <div data-id="element-21039">
                      <div className="text-sm font-bold text-slate-900" data-id="element-21040">
                        Новолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-21041">в Рыбах</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-21042">
                    10 мар
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100" data-id="element-21043">
              <div className="flex items-start gap-3" data-id="element-21044">
                <Info className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-21045" />
                <div data-id="element-21046">
                  <h4 className="font-bold text-indigo-900 mb-2" data-id="element-21047">
                    Что такое Луна без курса?
                  </h4>
                  <p className="text-sm text-indigo-800/80 leading-relaxed" data-id="element-21048">
                    Это период, когда Луна сделала свой последний мажорный
                    аспект в одном знаке зодиака, но еще не перешла в следующий.
                    В это время дела часто идут впустую, планы срываются, а
                    начатые проекты не приносят ожидаемого результата. Лучше
                    посвятить это время рутине и отдыху.
                  </p>
                </div>
              </div>
            </div>

            {/* Cross-links */}
            <div className="space-y-3" data-id="element-21049">
              <button onClick={() => onNavigate('daily-horoscope')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-21050">
                <span className="text-xl" data-id="element-21051">☀️</span>
                <div data-id="element-21052">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-21053">
                    Гороскоп на сегодня
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-21054">
                    Прогноз для вашего знака
                  </div>
                </div>
              </button>
              <button onClick={() => onNavigate('calendar')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-21055">
                <span className="text-xl" data-id="element-21056">📅</span>
                <div data-id="element-21057">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-21058">
                    Астро-календарь
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-21059">
                    Затмения и ретрограды
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-21060">
          <AdSlot slotId="SLOT-11" onNavigate={onNavigate} data-id="element-21061" />
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-21062">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-21063" />
          <div className="relative z-10" data-id="element-21064">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-21065">
              Узнайте, как Луна влияет именно на вас
            </h2>
            <p className="text-indigo-200 mb-8 max-w-xl mx-auto" data-id="element-21066">
              Постройте свою натальную карту и получите персональный анализ
              лунных транзитов.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-21067">
              <button onClick={() => onNavigate('service-natal')} className="px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors" data-id="element-21068">
                Заказать натальную карту
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors" data-id="element-21069">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}